import { useGetEmailTemplatePreviewQuery } from '@codezo/graphql';
import { useState } from 'react';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Tabs } from '../../components/ui/tabs';

/**
 * One email, rendered as a recipient receives it.
 *
 * Previews use representative sample data, never a live order or offer — so
 * this shows what an email looks like, not what any particular customer got.
 * That sample data, and the sender, both come from the real send layer, so the
 * gallery can't drift from what actually goes out.
 */
export function EmailTemplatePreviewDialog({
	templateKey,
	onClose,
}: {
	/** Null closes the dialog. */
	templateKey: string | null;
	onClose: () => void;
}) {
	const [view, setView] = useState('html');

	const { data, loading, error } = useGetEmailTemplatePreviewQuery({
		variables: { key: templateKey ?? '' },
		skip: !templateKey,
		fetchPolicy: 'no-cache',
	});

	const preview = data?.getEmailTemplatePreview;

	return (
		<Dialog
			open={!!templateKey}
			onOpenChange={(open) => {
				if (!open) {
					setView('html');
					onClose();
				}
			}}
		>
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>Email preview</DialogTitle>
					<DialogDescription>
						{preview?.description || 'Rendered with representative sample data.'}
					</DialogDescription>
				</DialogHeader>
				<DialogBody>
					<div className="flex flex-col gap-4">
						{/*
						 * The header goes above the body, not into the dialog title,
						 * because the subject and the preview line are the two things
						 * most likely to be wrong and neither appears anywhere in the
						 * email itself. An admin reviewing only the body is checking
						 * the part least likely to have a problem.
						 */}
						{preview && !loading && <InboxHeader preview={preview} />}

						<Tabs
							value={view}
							onChange={setView}
							items={[
								{ value: 'html', label: 'HTML' },
								{ value: 'text', label: 'Plain text' },
							]}
						/>

						{loading && (
							<div className="h-96 animate-pulse rounded-lg bg-surface-fill" />
						)}

						{error && !loading && (
							<p className="py-10 text-center text-sm text-text-3">
								{error.message || 'This template could not be loaded'}
							</p>
						)}

						{preview && !loading && view === 'html' && (
							/*
							 * srcdoc in a sandbox with no allow-same-origin: the email is
							 * a complete document with its own styles, and injecting it
							 * would leak those into the admin app and defeat the point,
							 * which is seeing it as a mail client renders it. It needs to
							 * display, not to run scripts or reach the parent page.
							 */
							<iframe
								title={`${preview.key} email preview`}
								srcDoc={preview.html}
								sandbox=""
								className="h-[28rem] w-full rounded-lg border border-line-1 bg-white"
							/>
						)}

						{preview && !loading && view === 'text' && (
							// What text-only clients show, and what many screen readers get.
							<pre className="scroll-thin h-[28rem] overflow-auto rounded-lg border border-line-1 bg-surface-sunken p-4 text-sm whitespace-pre-wrap text-text-2">
								{preview.text}
							</pre>
						)}
					</div>
				</DialogBody>
			</DialogContent>
		</Dialog>
	);
}

/* ── What lands in the inbox, before anything is opened ── */

function HeaderField({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex gap-3 px-4 py-2">
			<span className="w-24 shrink-0 pt-0.5 text-xs font-medium tracking-[0.06em] text-text-4 uppercase">
				{label}
			</span>
			<div className="min-w-0 flex-1">{children}</div>
		</div>
	);
}

function InboxHeader({
	preview,
}: {
	preview: {
		fromName: string;
		fromEmail: string;
		subject: string;
		preheader?: string | null;
	};
}) {
	return (
		<div className="divide-y divide-line-1 rounded-lg border border-line-2">
			<HeaderField label="From">
				<span className="text-sm text-text-1">{preview.fromName}</span>{' '}
				<span className="text-sm text-text-4">&lt;{preview.fromEmail}&gt;</span>
			</HeaderField>

			<HeaderField label="Subject">
				<span className="text-base font-medium text-text-1">{preview.subject}</span>
			</HeaderField>

			<HeaderField label="Preview text">
				{preview.preheader ? (
					<span className="text-sm text-text-2">{preview.preheader}</span>
				) : (
					/*
					 * Flagged rather than shown blank: the preheader is rendered into
					 * a hidden element, so a missing one is invisible in the email and
					 * in every test send. Mail clients fill the gap with whatever text
					 * comes first in the body.
					 */
					<span className="text-sm text-status-warn">
						Not set — mail clients will show the first line of the body
					</span>
				)}
			</HeaderField>
		</div>
	);
}
